import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import jubilutAvatar from '../../../../assets/images/mostra-seu-aprova/jubilut-avatar.png';
import sparkle from '../../../../assets/images/mostra-seu-aprova/sparkle.svg';
import { useUserStore } from '../../../../modules/Auth/stores/authStore';
import PlayerVimeo from '../../atoms/PlayerVimeo';
import {
  MOSTRA_SEU_APROVA_AUTHOR,
  MOSTRA_SEU_APROVA_CONTENTS,
  MOSTRA_SEU_APROVA_CTA_LABEL,
  MOSTRA_SEU_APROVA_DISMISS_LABEL,
  MOSTRA_SEU_APROVA_HASHTAG,
  MOSTRA_SEU_APROVA_THEMES,
  MostraSeuAprovaKey,
  iMostraSeuAprovaContent,
} from './data';

interface iMostraSeuAprovaModalProps {
  /** Chave da variação (ex.: `flashcards`) ou um conteúdo customizado. */
  content: MostraSeuAprovaKey | iMostraSeuAprovaContent;
  isOpen: boolean;
  /** Chamado ao fechar por ESC, clique fora ou pelo "Agora não". */
  onClose: () => void;
  /** Chamado ao clicar no botão primário, antes de fechar. */
  onCtaClick?: (content: iMostraSeuAprovaContent) => void;
  /** Se informado, abre a URL em uma nova aba ao clicar no botão primário. */
  ctaUrl?: string;
  className?: string;
}

function MostraSeuAprovaModal({
  content,
  isOpen,
  onClose,
  onCtaClick,
  ctaUrl,
  className = '',
}: iMostraSeuAprovaModalProps) {
  const { user } = useUserStore();

  const data =
    typeof content === 'string' ? MOSTRA_SEU_APROVA_CONTENTS[content] : content;

  const theme = MOSTRA_SEU_APROVA_THEMES[data.theme];

  /**
   * A apresentação da campanha é só o vídeo: sem card, texto ou botões — quem
   * apresenta é o próprio Jubilut, na tela. As variações por produto seguem no
   * layout de texto.
   */
  const hasVideo = !!data.vimeoToken;

  /**
   * O par de botões é o mesmo nos dois layouts, mas o "Agora não" muda de cor:
   * no card ele fica sobre o fundo claro do tema, no vídeo sobre o escurecido
   * do diálogo.
   */
  const renderActions = (dismissClassName: string) => (
    <>
      <button
        type='button'
        onClick={handleCtaClick}
        className='w-full rounded-[999px] border-[3px] border-solid border-[#000] bg-[#FF5227] p-[15px] text-center font-poppins text-[15px] font-extrabold uppercase text-[#FCFCFC] shadow-[4px_4px_0px_0px_#000]'
      >
        {MOSTRA_SEU_APROVA_CTA_LABEL}
      </button>

      <button
        type='button'
        onClick={onClose}
        className={`w-full py-[12px] text-center font-poppins text-[13px] font-bold underline ${dismissClassName}`}
      >
        {MOSTRA_SEU_APROVA_DISMISS_LABEL}
      </button>
    </>
  );

  const handleCtaClick = () => {
    onCtaClick?.(data);

    if (ctaUrl) {
      window.open(ctaUrl, '_blank', 'noopener,noreferrer');
    }

    onClose();
  };

  const renderVideoPanel = () => (
    <Dialog.Panel
      className={`relative my-auto transition-all ${className}`}
      /*
       * O vídeo da campanha é vertical (9:16). Num quadro widescreen ele
       * apareceria ladeado por faixas pretas, então o painel acompanha a
       * proporção do vídeo.
       *
       * A largura sai do menor entre três limites, e é ela que define a altura
       * do vídeo pela proporção: um teto fixo para telas grandes, a largura da
       * janela em telas estreitas e o espaço vertical que sobra depois dos
       * botões e da moldura do card. Sem o último, o conjunto ficaria mais alto
       * que a tela e o modal passaria a rolar.
       *
       * A parcela somada no fim devolve o que a moldura consome na horizontal,
       * então mexer no padding do card pede o mesmo ajuste ali — do contrário é
       * o vídeo que encolhe.
       */
      style={{ width: 'min(528px, 90vw, calc((92vh - 158px) * 9 / 16 + 92px))' }}
    >
      {/* O diálogo continua precisando de um nome acessível, mesmo sem texto visível. */}
      <Dialog.Title className='sr-only'>{data.title}</Dialog.Title>

      <div
        className='relative overflow-hidden rounded-[22px] border-[3px] border-solid border-[#000] px-10 py-4'
        style={{
          backgroundColor: theme.background,
          color: theme.text,
          boxShadow: `8px 8px 0px 0px ${theme.shadow}`,
        }}
      >
        <div
          aria-hidden
          className='pointer-events-none absolute inset-0'
          style={theme.pattern}
        />

        <div className='relative aspect-[9/16] w-full overflow-hidden rounded-[12px] border-[3px] border-solid border-[#000] bg-[#000]'>
          {/*
            Direto no atom, sem passar pelo `Player`: aquele sobrepõe a marca
            d'água com o ID do aluno nos primeiros 60s, que é proteção de
            videoaula e não faz sentido num vídeo de campanha.

            A qualidade respeita a preferência do aluno (quem escolheu baixa
            costuma ter motivo), mas a velocidade é sempre 1x: isto é um recado
            curto, não uma aula para assistir acelerado.
          */}
          <PlayerVimeo
            token={data.vimeoToken!}
            initialTime={0}
            className='h-full'
            videoQuality={user?.videoQuality || '720'}
            videoSpeed='1'
          />
        </div>

        <div className='relative flex flex-col gap-[12px] pt-[16px]'>
          {renderActions('')}
        </div>
      </div>

      <span
        aria-hidden
        className='pointer-events-none absolute right-[37px] top-[-14px] h-[46px] w-[46px] rotate-[14deg]'
        style={{
          backgroundColor: theme.sparkle,
          WebkitMaskImage: `url(${sparkle})`,
          maskImage: `url(${sparkle})`,
          WebkitMaskSize: '100% 100%',
          maskSize: '100% 100%',
        }}
      />
    </Dialog.Panel>
  );

  const renderTextPanel = () => (
    <Dialog.Panel
      className={`relative my-auto w-full max-w-[500px] transition-all ${className}`}
    >
      <div
        className='relative flex flex-col justify-between overflow-hidden rounded-[22px] border-[3px] border-solid border-[#000] p-6 sm:h-[500px] sm:p-9'
        style={{
          backgroundColor: theme.background,
          color: theme.text,
          boxShadow: `8px 8px 0px 0px ${theme.shadow}`,
        }}
      >
        <div
          aria-hidden
          className='pointer-events-none absolute inset-0'
          style={theme.pattern}
        />

        <div className='relative'>
          <div className='flex items-center gap-[10px]'>
            <div className='h-[34px] w-[34px] shrink-0 overflow-hidden rounded-[999px] border-[2px] border-solid border-[#000] bg-[#1B229F]'>
              <img
                src={jubilutAvatar}
                alt={MOSTRA_SEU_APROVA_AUTHOR}
                className='h-full w-full object-cover'
              />
            </div>

            <span className='font-poppins text-[13px] font-extrabold'>
              {MOSTRA_SEU_APROVA_AUTHOR}
            </span>

            <span className='-rotate-2 rounded-[6px] bg-[#000] px-[10px] py-[6px] font-poppins text-[10px] font-extrabold uppercase tracking-[0.8px] text-[#FFF200]'>
              {MOSTRA_SEU_APROVA_HASHTAG}
            </span>
          </div>

          <div className='pt-[20px]'>
            <p className='font-poppins text-[15px] font-bold leading-[1.35] opacity-85'>
              {data.eyebrow}
            </p>

            <Dialog.Title
              as='h2'
              className='pt-[10px] font-poppins text-[24px] font-black uppercase leading-[0.98] tracking-[-0.98px] sm:text-[28px]'
            >
              {data.title}
            </Dialog.Title>

            <p className='pt-[14px] font-notoSans text-[16px] font-medium leading-[1.5]'>
              {data.body}
            </p>
          </div>
        </div>

        <div className='relative flex flex-col gap-[12px] pt-8 sm:pt-0'>
          {renderActions('')}
        </div>
      </div>

      <span
        aria-hidden
        className='pointer-events-none absolute right-[37px] top-[-14px] h-[46px] w-[46px] rotate-[14deg]'
        style={{
          backgroundColor: theme.sparkle,
          WebkitMaskImage: `url(${sparkle})`,
          maskImage: `url(${sparkle})`,
          WebkitMaskSize: '100% 100%',
          maskSize: '100% 100%',
        }}
      />
    </Dialog.Panel>
  );

  return (
    <Transition appear show={isOpen} as={Fragment}>
      {/*
        Acima de todo o resto: este modal é montado no topo da aplicação, antes
        da árvore de rotas, então com o z-index padrão qualquer modal de página
        renderizado depois ficaria por cima dele. O maior valor em uso no
        projeto é 9999 (modais do Memory).
      */}
      <Dialog as='div' className='relative z-[10000]' onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-mono-color-black/70' />
        </Transition.Child>

        <div className='fixed inset-0 flex items-center justify-center overflow-y-auto p-4'>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 scale-95'
            enterTo='opacity-100 scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 scale-100'
            leaveTo='opacity-0 scale-95'
          >
            {hasVideo ? renderVideoPanel() : renderTextPanel()}
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

export { MostraSeuAprovaModal };
